import SmkAsyncSelectGraphql from 'components/SmkAsyncSelect/customSelectGraphql';
import { FOOD_ITEMS_SEARCH } from 'containers/nutrition/graphql/food_item.query';
import { RECIPE_SEARCH } from 'containers/nutrition/graphql/recipe.query';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { MEAL_TIME, WEEK_DAY_OPTIONS } from 'utils/constants';
import { titleCaseIfExists } from 'utils/helper';
import useRequest from 'hooks/useRequest';

const MEAL_TIME_OPTIONS = Object.keys(MEAL_TIME)?.map((e) => ({
  label: titleCaseIfExists(e),
  value: MEAL_TIME[e],
}));

const MultipleItems = ({ unitsList, formik, keyName, dietPlanDetails }) => {
  const [item, setItem] = useState({
    isFoodItem: false,
    foodItem: '',
    recipe: '',
    amount: '',
    amountUnit: null,
    calories: '',
    mealTime: null,
    weekDay: null,
  });

  const [items, setItems] = useState([]);

  const [addItemRequest] = useRequest({
    method: 'PUT',
    onSuccess: (data) => {
      toast.success('Item added successfully');
      refresh();
      setItem({
        isFoodItem: item.isFoodItem,
        foodItem: '',
        recipe: '',
        amount: '',
        amountUnit: null,
        calories: '',
        mealTime: null,
        weekDay: null,
      });
    },
    onError: () => {
      toast.error('Failed to add item');
    },
  });

  const handleAddItem = () => {
    const isValid =
      (item.foodItem || item.recipe) &&
      item.amount &&
      item.amountUnit &&
      item.calories &&
      item.mealTime &&
      item.weekDay;

    if (!isValid) {
      toast.error('Please fill all fields before adding!');
      return;
    }

    addItemRequest({
      url: `/nutrition/diet-plan/admin/${dietPlanDetails?.id}/add-item`,
      body: {
        mealTime: item.mealTime,
        weekDay: item.weekDay,
        foodItemId: item.foodItem?.id || null,
        recipeId: item.recipe?.id || null,
        amount: item.amount,
        amountUnit: item.amountUnit,
        calories: item.calories,
        isRawFood: false,
        mealPlanId: item?.mealPlan?.id || null,
      },
    });
  };

  const [deleteItemRequest] = useRequest({
    method: 'DELETE',
    onSuccess: () => {
      refresh();
      toast.success('Item deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete item');
    },
  });

  const [getItemRequest] = useRequest({
    method: 'GET',
    onSuccess: (data) => {
      setItems(data || []);
    },
    onError: () => {
      toast.error('Failed to fetch items');
    },
  });

  const refresh = () => {
    getItemRequest({
      url: `/nutrition/diet-plan/admin/${dietPlanDetails?.id}/items`,
    });
  };

  const handleDeleteItem = (itemId) => {
    deleteItemRequest({
      url: `/nutrition/diet-plan/admin/item/${itemId}`,
      meta: { itemId },
    });
  };

  useEffect(() => {
    if (dietPlanDetails?.id) refresh();
  }, [dietPlanDetails?.id]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setItem((prev) => ({
      ...prev,
      [name]: type === 'number' && value !== '' ? Number(value) : value,
    }));
  };

  const WEEK_DAY_ORDER = WEEK_DAY_OPTIONS.map((d) => d.value);
  const MEAL_TIME_ORDER = Object.values(MEAL_TIME);

  const sortedItems = [...items].sort((a, b) => {
    const weekDayDiff = WEEK_DAY_ORDER.indexOf(a.weekDay) - WEEK_DAY_ORDER.indexOf(b.weekDay);

    if (weekDayDiff !== 0) return weekDayDiff;

    return MEAL_TIME_ORDER.indexOf(a.mealTime) - MEAL_TIME_ORDER.indexOf(b.mealTime);
  });

  const groupedItems = WEEK_DAY_OPTIONS.map((day) => {
    const dayItems = sortedItems.filter((i) => i.weekDay === day.value);

    if (isEmpty(dayItems)) return null;

    let dayCalories = 0;

    const meals = MEAL_TIME_ORDER.map((meal) => {
      const mealItems = dayItems.filter((i) => i.mealTime === meal);

      if (!mealItems.length) return null;

      const mealCalories = mealItems.reduce((sum, i) => sum + Number(i.calories || 0), 0);

      dayCalories += mealCalories;

      return {
        mealTime: meal,
        items: mealItems,
        mealCalories,
      };
    }).filter(Boolean);

    return meals.length
      ? {
          day,
          meals,
          dayCalories,
        }
      : null;
  }).filter(Boolean);

  return (
    <Card className="mb-4">
      <CardBody>
        <h5 className="bg-light p-3 mb-4 text-center rounded-2">Items</h5>
        <div className="border rounded-2 p-4 mb-4">
          <Row className="align-items-center">
            <Col md={12}>
              {[true, false].map((option, i) => (
                <div key={i} className="form-check mb-3 d-inline-block me-4">
                  <input
                    type="radio"
                    className="form-check-input"
                    checked={item.isFoodItem === option}
                    onChange={() =>
                      setItem((prev) => ({
                        ...prev,
                        isFoodItem: option,
                        foodItem: option ? prev.foodItem : '',
                        recipe: option ? '' : prev.recipe,
                      }))
                    }
                  />
                  <label className="form-check-label">{option ? 'Food Item' : 'Recipe'}</label>
                </div>
              ))}
            </Col>

            {item.isFoodItem ? (
              <Col md={12}>
                <FormGroup className="mb-4">
                  <Label>Food Item *</Label>
                  <SmkAsyncSelectGraphql
                    acceptedKey="name"
                    acceptedValue="id"
                    searchKeyName="name"
                    query={FOOD_ITEMS_SEARCH}
                    queryName="admin_nutrition_foodItem_search"
                    dtoName="searchDTO"
                    filters={{ autoComplete: true, active: true }}
                    value={
                      !isEmpty(item.foodItem)
                        ? {
                            label: item.foodItem?.name,
                            value: item.foodItem?.id,
                          }
                        : null
                    }
                    onChange={(data) => setItem((p) => ({ ...p, foodItem: data }))}
                  />
                </FormGroup>
              </Col>
            ) : (
              <Col md={12}>
                <FormGroup className="mb-4">
                  <Label>Recipe *</Label>
                  <SmkAsyncSelectGraphql
                    acceptedKey="name"
                    acceptedValue="id"
                    searchKeyName="name"
                    query={RECIPE_SEARCH}
                    queryName="admin_nutrition_recipe_search"
                    dtoName="searchDTO"
                    filters={{ autoComplete: true, active: true }}
                    value={
                      !isEmpty(item.recipe)
                        ? {
                            label: item.recipe?.name,
                            value: item.recipe?.id,
                          }
                        : null
                    }
                    onChange={(data) => setItem((p) => ({ ...p, recipe: data }))}
                  />
                </FormGroup>
              </Col>
            )}

            <Col md={4}>
              <FormGroup className="mb-4">
                <Label>Meal Time *</Label>
                <Select
                  options={MEAL_TIME_OPTIONS}
                  value={MEAL_TIME_OPTIONS.find((o) => o.value === item.mealTime) || null}
                  onChange={(e) => setItem((p) => ({ ...p, mealTime: e?.value || null }))}
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup className="mb-4">
                <Label>Week Day *</Label>
                <Select
                  options={WEEK_DAY_OPTIONS}
                  value={WEEK_DAY_OPTIONS.find((o) => o.value === item.weekDay) || null}
                  onChange={(e) => setItem((p) => ({ ...p, weekDay: e?.value || null }))}
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup className="mb-4">
                <Label>Amount *</Label>
                <Input name="amount" value={item.amount} onChange={handleChange} />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup className="mb-4">
                <Label>Amount Unit *</Label>
                <Select
                  options={unitsList}
                  value={unitsList.find((o) => o.value === item.amountUnit) || null}
                  onChange={(e) => setItem((p) => ({ ...p, amountUnit: e?.value || null }))}
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup className="mb-4">
                <Label>Calories *</Label>
                <Input type="number" name="calories" value={item.calories} onChange={handleChange} />
              </FormGroup>
            </Col>
          </Row>

          <Button color="dark" className="w-100 mt-3" onClick={handleAddItem}>
            Add Item
          </Button>
        </div>

        {!isEmpty(items) && (
          <div className="border rounded-2 p-4 bg-light">
            <h6 className="mb-3">Added Items:</h6>
            {groupedItems.map(({ day, meals, dayCalories }) => (
              <div key={day.value} className="mb-5">
                <h5 className="mb-3 border-bottom pb-2 d-flex justify-content-between">
                  <span>{day.label}</span>
                  <span className="text-muted">Total: {dayCalories} kcal</span>
                </h5>

                {meals.map(({ mealTime, items, mealCalories }) => (
                  <div key={mealTime} className="mb-4">
                    <h6 className="mb-3 text-primary d-flex justify-content-between">
                      <span>{titleCaseIfExists(mealTime.replace('_', ' '))}</span>
                      <span className="text-muted">{mealCalories} kcal</span>
                    </h6>

                    <div className="row">
                      {items.map((p) => (
                        <div className="col-md-4" key={p.id}>
                          <div className="border p-3 rounded-2 mb-4 bg-white">
                            <strong>{p.foodItem?.name || p.recipe?.name}</strong>

                            <div>
                              {p.amount} {p.amountUnit}
                            </div>

                            <div>Type: {p.recipeId ? 'Recipe' : p?.mealPlanId ? 'Meal Plan' : 'Food Item'}</div>

                            <div>Calories: {p.calories}</div>

                            <Button
                              className="w-100 mt-2"
                              color="danger"
                              size="sm"
                              onClick={() => handleDeleteItem(p.id)}>
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default MultipleItems;
